import { useId, type JSX } from "react";
import { cx } from "@web/booking/ui";

export function HeroSwoosh({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 600"
      preserveAspectRatio="none"
      fill="none"
      className={cx("pointer-events-none text-surface rtl:-scale-x-100", className)}
    >
      <path
        d="M600 40C380 140 260 330 330 600"
        stroke="currentColor"
        strokeWidth="60"
        opacity="0.7"
      />
      <path
        d="M600 140C430 230 360 400 420 600"
        stroke="currentColor"
        strokeWidth="24"
        opacity="0.5"
      />
    </svg>
  );
}

export function HeroRibbon({ className }: { readonly className?: string }): JSX.Element {
  const band = useId();
  const sheen = useId();

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      fill="none"
      className={cx("pointer-events-none ltr:-scale-x-100", className)}
    >
      <defs>
        <linearGradient id={band} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" style={{ stopColor: "var(--color-primary-100)", stopOpacity: 0.2 }} />
          <stop offset="0.3" style={{ stopColor: "var(--color-primary-200)", stopOpacity: 0.85 }} />
          <stop
            offset="0.75"
            style={{ stopColor: "var(--color-primary-500)", stopOpacity: 0.85 }}
          />
          <stop offset="1" style={{ stopColor: "var(--color-primary-700)", stopOpacity: 0.6 }} />
        </linearGradient>
        <linearGradient id={sheen} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: "var(--color-surface)", stopOpacity: 0 }} />
          <stop offset="0.5" style={{ stopColor: "var(--color-surface)", stopOpacity: 0.7 }} />
          <stop offset="1" style={{ stopColor: "var(--color-surface)", stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path
        d="M96 -2C109 24 112 50 107 71C103 86 96 96 90 103"
        stroke="var(--color-primary-200)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M86.5 -2C97.5 22 101.5 46 97.5 66.5C94.5 82 88 93 81.5 102L80 102C86 93 92.5 82 95.5 66.5C99.5 46 95.5 22 84.5 -2Z"
        fill="var(--color-surface)"
        opacity="0.9"
      />
      <path
        d="M87 -2C98 22 102 46 98 66.5C95 82 88.5 93 82 102L88 102C95 92 102 80 104.5 64C107.5 43 102 20 91 -2Z"
        fill={`url(#${band})`}
      />
      <path
        d="M89 6C98 26 101.5 48 98.5 66C96 80 91 90 86 97C92.5 89 98.5 78 101 64C104 46 99.5 25 89 6Z"
        fill={`url(#${sheen})`}
      />
    </svg>
  );
}

export function HeroDipRibbon({ className }: { readonly className?: string }): JSX.Element {
  const band = useId();

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      fill="none"
      className={cx("pointer-events-none ltr:-scale-x-100", className)}
    >
      <defs>
        <linearGradient id={band} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: "var(--color-primary-200)", stopOpacity: 0.2 }} />
          <stop offset="0.5" style={{ stopColor: "var(--color-primary-500)", stopOpacity: 0.7 }} />
          <stop offset="1" style={{ stopColor: "var(--color-primary-700)", stopOpacity: 0.9 }} />
        </linearGradient>
      </defs>
      <path
        d="M0 91C25 94 45 94 60 87C72 81 86 74 100 72L100 79C86 81 72 88 60 93C45 100 25 100 0 96Z"
        style={{ fill: "var(--color-primary-100)", fillOpacity: 0.7 }}
      />
      <path
        d="M0 89C25 91 45 87 60 77C72 69 86 61 100 59L100 71C86 72 72 79 60 86C45 94 25 93 0 90Z"
        fill={`url(#${band})`}
      />
    </svg>
  );
}

export function WaveRibbon({ className }: { readonly className?: string }): JSX.Element {
  const band = useId();

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      fill="none"
      className={cx("pointer-events-none ltr:-scale-x-100", className)}
    >
      <defs>
        <linearGradient id={band} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: "var(--color-primary-200)", stopOpacity: 0.4 }} />
          <stop offset="0.5" style={{ stopColor: "var(--color-primary-500)", stopOpacity: 0.85 }} />
          <stop offset="1" style={{ stopColor: "var(--color-primary-200)", stopOpacity: 0.4 }} />
        </linearGradient>
      </defs>
      <path
        d="M0 88C20 99 38 99 52 92C66 84 82 82 100 89L100 93C82 87 66 89 52 96C38 100 20 100 0 92Z"
        style={{ fill: "var(--color-primary-100)", fillOpacity: 0.7 }}
      />
      <path
        d="M0 81C20 93 38 93 52 83C66 73 82 71 100 81L100 85C82 77 66 80 52 90C38 99 20 98 0 85Z"
        fill={`url(#${band})`}
      />
    </svg>
  );
}

export function HeroWave({ className }: { readonly className?: string }): JSX.Element {
  const fill = useId();

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 600 160"
      preserveAspectRatio="none"
      className={cx("pointer-events-none ltr:-scale-x-100", className)}
    >
      <defs>
        <linearGradient id={fill} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" style={{ stopColor: "var(--color-primary-500)", stopOpacity: 0.35 }} />
          <stop offset="1" style={{ stopColor: "var(--color-primary-100)", stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d="M0 40C160 120 360 150 600 110V160H0Z" fill={`url(#${fill})`} />
    </svg>
  );
}

export function GlassLobe({ className }: { readonly className?: string }): JSX.Element {
  const glass = useId();

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      fill="none"
      className={cx("pointer-events-none ltr:-scale-x-100", className)}
    >
      <defs>
        <linearGradient id={glass} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" style={{ stopColor: "var(--color-primary-50)", stopOpacity: 0.95 }} />
          <stop offset="0.45" style={{ stopColor: "var(--color-primary-100)", stopOpacity: 0.9 }} />
          <stop offset="1" style={{ stopColor: "var(--color-primary-200)", stopOpacity: 0.9 }} />
        </linearGradient>
      </defs>
      <path
        d="M76 -2C89 -3 102 4 104 18C106 38 102 68 82 104"
        stroke="var(--color-primary-200)"
        strokeWidth="1.5"
        opacity="0.8"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M70 0C80 -1 92 2 98 8C102 14 101 30 100 44C99 64 92 84 72 102L66 100C86 84 94 62 93 42C92 24 84 8 74 0Z"
        fill={`url(#${glass})`}
      />
      <path
        d="M70 0C80 -1 92 2 98 8C102 14 101 30 100 44C99 64 92 84 72 102"
        stroke="var(--color-surface)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M74 0C84 8 92 24 93 42C94 62 86 84 66 100"
        stroke="var(--color-primary-300)"
        strokeWidth="1.5"
        opacity="0.7"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function BandWave({ className }: { readonly className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      className={cx("block", className)}
    >
      <path d="M0 70C260 10 520 20 780 64S1220 118 1440 40V120H0Z" fill="currentColor" />
    </svg>
  );
}
